import { type ICryptoChannel } from './crypto-channel.types';

function reverseString(str: string): string
{
    return str.split('').reverse().join('');
}

const sharedKeyXDefault =
    '21749580627744043490293460885184921736187892537578287943095433020419112973523';
const sharedKeyYDefault =
    '64549560279114833959357227200374692973440838462517619633445557604010912723288';

export class CryptoChannel implements ICryptoChannel
{
    private aesKey!: CryptoKey;
    private aesIV!: ArrayBuffer;

    private readonly window: Window = window;

    constructor(
        private readonly SharedKeyX: string = sharedKeyXDefault,
        private readonly SharedKeyY: string = sharedKeyYDefault,
    ) {}

    public async ApplySecurityCode(securityCode: string): Promise<void>
    {
        const code = securityCode.padEnd(8, '_');

        const password = this.SharedKeyX + this.SharedKeyY;

        const pwdKey = await this.window.crypto.subtle.importKey(
            'raw',
            new TextEncoder().encode(password),
            { name: 'PBKDF2' },
            false,
            [ 'deriveBits', 'deriveKey' ],
        );

        const pwdKeyForAesIV = await this.window.crypto.subtle.importKey(
            'raw',
            new TextEncoder().encode(this.SharedKeyX),
            { name: 'PBKDF2' },
            false,
            [ 'deriveBits', 'deriveKey' ],
        );

        this.aesKey = await this.window.crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                iterations: 1000,
                hash: 'SHA-1',
                salt: new TextEncoder().encode(code),
            },
            pwdKey,
            { name: 'AES-CBC', length: 256 },
            true,
            [ 'encrypt', 'decrypt' ],
        );

        const key = await this.window.crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                iterations: 1000,
                hash: 'SHA-1',
                salt: new TextEncoder().encode(reverseString(code)),
            },
            pwdKeyForAesIV,
            { name: 'AES-CBC', length: 256 },
            true,
            [ 'encrypt', 'decrypt' ],
        );

        const aesIV = await this.window.crypto.subtle.exportKey('raw', key);
        this.aesIV = aesIV.slice(0, 16);
    }

    public async DecryptMessage(encrypted: string, convertBytesToBase64?: boolean): Promise<any>
    {
        const decMsg = await this.decryptAES(encrypted, convertBytesToBase64);
        return JSON.parse(decMsg);
    }

    public async EncryptMessage(data: string, convertBytesToBase64: boolean = false): Promise<string>
    {
        const messageStr = JSON.stringify(data);
        const encrypted = await this.encryptAES(messageStr, convertBytesToBase64);
        return encrypted;
    }

    public async DecryptObject<T>(encrypted: string): Promise<T>
    {
        const decMsg = await this.decryptAES(encrypted, false);
        return JSON.parse(decMsg);
    }

    public async EncryptObject(data: any): Promise<string>
    {
        const messageStr = JSON.stringify(data);
        const encrypted = await this.encryptAES(messageStr, false);
        return encrypted;
    }

    private async encryptAES(data: string, isBase64: boolean = false): Promise<string>
    {
        if (!this.aesKey || !this.aesIV)
        {
            return data;
        }

        if (!isBase64)
        {
            data = Buffer.from(data, 'utf-8').toString('base64');
        }

        const encrypted = await this.window.crypto.subtle.encrypt(
            {
                name: 'AES-CBC',
                iv: this.aesIV,
            },
            this.aesKey,
            new TextEncoder().encode(data),
        );

        return Buffer.from(encrypted).toString('base64');
    }

    private async decryptAES(data: string, isBase64: boolean = false): Promise<string>
    {
        if (!this.aesKey || !this.aesIV)
        {
            return data;
        }

        const decrypted = await this.window.crypto.subtle
            .decrypt(
                {
                    name: 'AES-CBC',
                    iv: this.aesIV,
                },
                this.aesKey,
                Buffer.from(data, 'base64'),
            )
            .catch(console.error);

        if (!decrypted)
        {
            console.trace();
            console.log(data);
            throw new Error('Decrypted Error');
        }

        const decryptedBase64 = Buffer.from(decrypted).toString('utf8');

        if (!isBase64)
        {
            return Buffer.from(decryptedBase64, 'base64').toString('utf-8');
        }

        return decryptedBase64;
    }
}
